// File: src/pages/reports/PurchaseOrderReport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Label,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";

/* ===== Your shared modules (per rules) ===== */
import {
  _Get,
  _Post,
  PurchaseOrderList as _PurchaseOrderList,
} from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import SuperAdminUI from "../import/SuperAdminUI";

/* ========= inline, page-scoped CSS ========= */
const injectOnce = () => {
  if (document.getElementById("por-inline-css")) return;
  const style = document.createElement("style");
  style.id = "por-inline-css";
  style.innerHTML = `
    .por-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .por-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .por-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:980px; }
    .por-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .por-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .por-table tbody tr:hover{ background:#f9fbff; }
    .por-dl{ position:relative; }
    .por-dl-menu{
      position:absolute; right:0; top:32px; z-index:5; border:1px solid rgb(8,91,245);
      background:#fff; border-radius:10px; overflow:hidden; min-width:130px; box-shadow:0 12px 24px rgba(0,0,0,.12);
    }
    .por-dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
  `;
  document.head.appendChild(style);
};

/* ========= helpers ========= */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const f2 = (n) =>
  n === null || n === undefined || isNaN(n) ? "" : Number(n).toFixed(2);
const formatDateOnly = (iso) => (iso ? String(iso).split("T")[0] : "");

/* ========= component ========= */
export default function PurchaseOrderReport() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: false,
    Edit: false,
    Delete: false,
    Download: true,
  });

  const [isMaster, setIsMaster] = useState(false);

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  const [search, setSearch] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);

  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  // Column chooser buffers
  const addBufferRef = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => injectOnce(), []);

  /* ===== Base columns (mirrors your old grid) ===== */
  const baseColumns = useMemo(
    () => [
      {
        key: "uid",
        header: "UID",
        get: (_r, idx) => idx + 1,
      },
      {
        key: "supplier",
        header: "SUPPLIER NAME",
        get: (r) => r?.order?.partyId?.CompanyName,
      },
      {
        key: "product",
        header: "PRODUCT NAME",
        get: (r) => r?.productId?.Product_Title,
      },
      {
        key: "hsn",
        header: "HSN",
        get: (r) => r?.productId?.HSN_Code,
      },
      {
        key: "qty",
        header: "QNTY",
        get: (r) => r?.qty,
      },
      {
        key: "taxableAmount",
        header: "TAXABLE",
        get: (r) => f2(r?.taxableAmount),
      },
      {
        key: "cgstRate",
        header: "CGST",
        get: (r) => f2(r?.cgstRate),
      },
      {
        key: "sgstRate",
        header: "SGST",
        get: (r) => f2(r?.sgstRate),
      },
      {
        key: "igstRate",
        header: "IGST",
        get: (r) => f2(r?.igstRate),
      },
      {
        key: "grandTotal",
        header: "TOTAL",
        get: (r) => f2(r?.grandTotal),
      },
    ],
    []
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  // restore visible columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("PurchaseReportamountList"); // keep your original key
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        // parsed may contain objects if it came from old grid; normalize to keys if needed
        if (typeof parsed[0] === "string") {
          nextKeys = parsed.filter((k) => colMap.has(k));
        } else if (typeof parsed[0] === "object") {
          // try to map by headerName
          const byHeader = new Map(
            baseColumns.map((c) => [c.header?.toUpperCase(), c.key])
          );
          nextKeys = parsed
            .map((obj) =>
              byHeader.get(String(obj?.headerName || "").toUpperCase())
            )
            .filter((k) => !!k && colMap.has(k));
        }
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [colMap, baseColumns]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  /* ===== Fetch ===== */
  const fetchData = async (userId, database) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const inside = CheckPermission("PURCHASE ORDER REPORT");
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }

      const res = await _PurchaseOrderList(userId, database);
      const completed = Array.isArray(res?.orderHistory)
        ? res.orderHistory.filter(
            (o) => String(o?.status).toLowerCase() === "completed"
          )
        : [];

      const flat = completed.flatMap((ord) =>
        Array.isArray(ord?.orderItems)
          ? ord.orderItems.map((itm) => ({ ...itm, order: ord }))
          : []
      );

      setRows(flat);
      setAllRows(flat);
    } catch (e) {
      console.error("[POR] fetch error:", e);
      setErrorMsg(
        e?.response?.data?.message || e?.message || "Failed to load report."
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  // bootstrap
  useEffect(() => {
    (async () => {
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        setIsMaster(user?.rolename?.roleName === "MASTER");
        await fetchData(user?._id, user?.database);
      } catch (e) {
        setLoading(false);
      }
    })();
  }, []);

  /* ===== Search + Date filters ===== */
  const filteredByDate = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return rows;
    return rows.filter((item) => {
      const dt = item?.order?.date ? formatDateOnly(item.order.date) : "";
      if (startDate && dt < startDate) return false;
      if (endDate && dt > endDate) return false;
      return true;
    });
  }, [rows, dateRange]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredByDate;
    const cols = visibleCols;
    return filteredByDate.filter((r, idx) =>
      cols.some((c) => toStr(c.get(r, idx)).toLowerCase().includes(term))
    );
  }, [filteredByDate, search, visibleCols]);

  /* ===== Column chooser ===== */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);
  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    // Save keys; keeps compatibility with prior storage by storing headers if you prefer
    localStorage.setItem(
      "PurchaseReportamountList",
      JSON.stringify(selectedKeys)
    );
    closeModal();
  };

  /* ===== MASTER → SuperAdminUI handlers ===== */
  const handleParentSubmit = async () => {
    const saved = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!saved) return swal("Please pick a SuperAdmin in the dropdown");
    let sid = "";
    let sdb = "";
    if (typeof saved === "string") {
      const parts = saved.split(" ");
      sid = parts[0];
      sdb = parts[1];
    } else if (Array.isArray(saved)) {
      sid = saved[0];
      sdb = saved[1];
    }
    if (!sid || !sdb) return swal("Invalid Master selection");
    await fetchData(sid, sdb);
  };
  const handleDropdownChange = (v) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(v));
  };

  /* ===== Export ===== */
  const handleExportPDF = async () => {
    const cols = visibleCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r, idx) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r, idx)).replace(/"/g, '""');
          return "${v}";
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "PurchaseOrderReport");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols;
    const data = filteredRows.map((r, idx) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r, idx))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  /* ===== UI ===== */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar card ===== */}
      <Row className="por-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Purchase Order Report{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 120px" }} />

              {/* MASTER selector */}
              {isMaster ? (
                <div style={{ minWidth: 260 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : null}

              {/* Search */}
              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {/* Date range */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <Label className="m-0">Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) =>
                      setDateRange((p) => ({ ...p, startDate: e.target.value }))
                    }
                    style={{ background: "#fff", minWidth: 160 }}
                  />
                </div>
                <div>
                  <Label className="m-0">End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) =>
                      setDateRange((p) => ({ ...p, endDate: e.target.value }))
                    }
                    style={{ background: "#fff", minWidth: 160 }}
                  />
                </div>
                <Button
                  style={{
                    cursor: "pointer",
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => setDateRange((p) => ({ ...p }))}
                >
                  Apply
                </Button>
              </div>

              {/* Column chooser */}
              {permissions?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openModal}
                  title="Filter Columns"
                  style={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFilter size={14} />
                </Button>
              )}

              {/* Download */}
              {permissions?.Download && (
                <div
                  className="por-dl"
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div className="por-dl-menu">
                      <div onClick={handleExportPDF} className="por-dl-item">
                        .PDF
                      </div>
                      <div
                        onClick={() => handleExportXLSX("PurchaseOrderReport")}
                        className="por-dl-item"
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table card ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="por-table-wrap">
                <table className="por-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, idx) => (
                      <tr
                        key={
                          r?._id || r?.id || `${r?.order?._id || "row"}-${idx}`
                        }
                      >
                        {visibleCols.map((c) => (
                          <td key={c.key}>{toStr(c.get(r, idx))}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column chooser modal ===== */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* small styles reused */
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
